import type { FlowContext, OrderSnapshot, SnapshotScope, StepRuntime, VerifyResult } from "../types";
import { FlowStepError } from "../engine/errors";

export const requireEnquiry = (ctx: FlowContext) => {
  if (!ctx.enquiry) {
    throw new FlowStepError("No enquiry exists yet; the enquiry step must run first.");
  }
  return ctx.enquiry;
};

export const requireChild = (ctx: FlowContext) => {
  if (!ctx.child) {
    throw new FlowStepError(
      "No confirmed order exists yet; confirm the estimate before running this step."
    );
  }
  return ctx.child;
};

export const requireEstimate = (ctx: FlowContext) => {
  if (!ctx.estimate) {
    throw new FlowStepError("No estimate exists yet; create the estimate first.");
  }
  return ctx.estimate;
};

export const verifyEq = (
  label: string,
  expected: string | string[],
  actual: string | undefined
): VerifyResult => {
  const expectedList = Array.isArray(expected) ? expected : [expected];
  return {
    ok: actual !== undefined && expectedList.includes(actual),
    expected: `${label} = ${expectedList.join(" | ")}`,
    actual: `${label} = ${actual ?? "undefined"}`,
  };
};

export const verifyTruthy = (label: string, value: unknown): VerifyResult => ({
  ok: Boolean(value),
  expected: `${label} present`,
  actual: value ? `${label} present` : `${label} missing`,
});

/** Polls the snapshot until `check` passes, surfacing the observed state. */
export const waitForSnapshot = (
  rt: StepRuntime,
  args: {
    trigger: string;
    expected: string;
    check: (s: OrderSnapshot) => boolean;
    describe: (s: OrderSnapshot) => string;
    maxAttempts?: number;
    intervalsMs?: number[];
    scope?: SnapshotScope;
  }
) => {
  const scope: SnapshotScope =
    args.scope ?? (rt.ctx.child ? { child: true } : { enquiry: true });
  return rt.poll<OrderSnapshot>({
    trigger: args.trigger,
    expected: args.expected,
    fetch: () => rt.refreshSnapshot(scope),
    predicate: args.check,
    describe: args.describe,
    maxAttempts: args.maxAttempts ?? rt.ctx.config.pollMaxAttempts,
    intervalsMs: args.intervalsMs,
  });
};

export const supplierTransitionPath = (orderId: string, action: string) =>
  `/v1/orders/${orderId}/transition?action=${action}`;

export const garaazTransitionPath = (orderId: string, action: string) =>
  `/v1/orders/${orderId}/garaaz_transition?action=${action}`;

export const orderItemsForSupplier = (ctx: FlowContext) => {
  const items = ctx.snapshot?.child?.orderItems || [];
  return items.map((item) => ({
    partId: item.partId,
    orderedQuantity: item.orderedQuantity,
    givenQuantity: item.orderedQuantity,
    ...(item.mrp ? { partMrp: item.mrp } : {}),
  }));
};

export const todayForInvoice = () => new Date().toISOString().slice(0, 10);

export const shortId = () =>
  `${Date.now().toString(36).slice(-5)}${Math.random().toString(36).slice(2, 5)}`.toUpperCase();
